/**
 * NeedsAttentionStrip — the OVERDUE next-action strip (Phase 3a, 2026-07-03).
 *
 * The one place a staffer sees which leads are OWED an action right now:
 * every lead whose `nextActionAt` (auto-set on stage entry by the state
 * machine's next-action engine, lib/leads) is due at/before now. Rows: lead
 * name · stage · the plain-language note · one-click open (`/leads/:id`, the
 * full-page record). Renders NOTHING when nothing is overdue — never an
 * empty box. Self-contained (own listenLeads subscription) so it drops onto
 * any surface (the /leads lenses shell + Home) without lifting state.
 *
 * No inner scroll (one page scroll): shows the 5 oldest-due + a "+N more in
 * Leads" link when longer. Tokens only; WCAG: a real list, real links.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { needsAttention, LEAD_STATE_LABEL, type Lead } from '../../lib/leads';
import { listenLeads } from '../../lib/leadStore';
import { Btn3, Tag } from '../ui';

const SHOW_MAX = 5;

/** "due 3 d ago" / "due 2 h ago" / "due now" (render-only). */
function dueAgo(atISO: string, nowISO: string): string {
  const ms = Date.parse(nowISO) - Date.parse(atISO);
  if (!Number.isFinite(ms) || ms < 60_000) return 'due now';
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return `due ${Math.floor(ms / 60_000)} min ago`;
  if (h < 24) return `due ${h} h ago`;
  return `due ${Math.floor(h / 24)} d ago`;
}

export default function NeedsAttentionStrip() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [nowISO] = useState(() => new Date().toISOString());
  useEffect(() => {
    const unsub = listenLeads(setLeads, () => setLeads([]));
    return unsub;
  }, []);

  const overdue = useMemo(() => needsAttention(leads, nowISO), [leads, nowISO]);
  if (overdue.length === 0) return null;

  const shown = overdue.slice(0, SHOW_MAX);
  const more = overdue.length - shown.length;

  return (
    <section
      aria-label="Leads needing attention"
      data-testid="needs-attention-strip"
      style={{
        border: '1px solid var(--warn-line, #FDE68A)',
        background: 'var(--warn-wash, #FEF3C7)',
        borderRadius: 'var(--r-md)',
        padding: '10px 14px',
        margin: '10px 12px',
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--warn-ink, #92400E)',
          marginBottom: 8,
        }}
      >
        Needs attention — {overdue.length} lead{overdue.length === 1 ? '' : 's'} overdue
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {shown.map((l) => (
          <li
            key={l.id}
            style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13 }}
          >
            <b style={{ color: 'var(--ink)' }}>{l.name || l.id}</b>
            <Tag kind="warn">{LEAD_STATE_LABEL[l.state]}</Tag>
            <span style={{ color: 'var(--ink-soft)', flex: '1 1 200px', minWidth: 0 }}>
              {l.nextActionNote || 'Follow up'}
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--warn-ink, #92400E)', whiteSpace: 'nowrap' }}>
              {dueAgo(l.nextActionAt as string, nowISO)}
            </span>
            <Btn3 intent="ghost" size="sm" onClick={() => navigate(`/leads/${l.id}`)}>
              Open →
            </Btn3>
          </li>
        ))}
      </ul>
      {more > 0 && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <button
            type="button"
            onClick={() => navigate('/leads')}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: 'var(--accent)',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          >
            +{more} more in Leads →
          </button>
        </div>
      )}
    </section>
  );
}
